import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AcademicCapIcon,
  PlayIcon,
  CheckCircleIcon,
  ClockIcon,
  StarIcon,
  EyeIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  UserGroupIcon,
  LightBulbIcon,
  ChartBarIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { PROGRESS_STATUS } from '../../services/programsService';

const ModuleViewer = ({
  program,
  modules,
  userProgress,
  onLessonStart,
  onLessonComplete,
  userRole,
}) => {
  const [expandedModule, setExpandedModule] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);

  const getModuleIcon = moduleType => {
    switch (moduleType) {
      case 'video':
        return VideoCameraIcon;
      case 'interactive':
        return UserGroupIcon;
      case 'document':
        return DocumentTextIcon;
      case 'quiz':
        return ChartBarIcon;
      default:
        return AcademicCapIcon;
    }
  };

  const getLessonStatus = lessonId => {
    if (!userProgress?.lessons) return 'not-started';
    const lesson = userProgress.lessons[lessonId];
    if (lesson?.completed) return 'completed';
    if (lesson?.started) return 'in-progress';
    return 'not-started';
  };

  const getStatusIcon = status => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'in-progress':
        return <PlayIcon className="h-5 w-5 text-blue-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateModuleProgress = moduleLessons => {
    if (!userProgress?.lessons || !moduleLessons) return 0;

    const completedLessons = moduleLessons.filter(lesson => {
      const lessonStatus = getLessonStatus(lesson.id);
      return lessonStatus === 'completed';
    });

    return Math.round((completedLessons.length / moduleLessons.length) * 100);
  };

  const toggleModuleExpansion = moduleId => {
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
  };

  const handleLessonAction = (lessonId, action) => {
    if (action === 'start') {
      onLessonStart(lessonId);
    } else if (action === 'complete') {
      onLessonComplete(lessonId);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Training Modules</h2>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <AcademicCapIcon className="h-4 w-4" />
          <span>{modules.length} Modules</span>
        </div>
      </div>

      <div className="rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 p-6">
        <div className="mb-4 flex items-center gap-3">
          <AcademicCapIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{program.title}</h3>
            <p className="text-gray-600">{program.description}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{modules.length}</div>
            <div className="text-sm text-gray-600">Total Modules</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {modules.filter(m => calculateModuleProgress(m.lessons) === 100).length}
            </div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {
                modules.filter(m => {
                  const progress = calculateModuleProgress(m.lessons);
                  return progress > 0 && progress < 100;
                }).length
              }
            </div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">
              {modules.filter(m => calculateModuleProgress(m.lessons) === 0).length}
            </div>
            <div className="text-sm text-gray-600">Not Started</div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {modules.map((module, index) => {
          const Icon = getModuleIcon(module.type);
          const moduleProgress = calculateModuleProgress(module.lessons);
          const isExpanded = expandedModule === module.id;

          return (
            <motion.div
              key={module.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
            >
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-blue-100 p-2">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{module.title}</h3>
                      <p className="text-sm text-gray-600">{module.description}</p>
                      <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                        <span>{module.duration}</span>
                        <span>{module.difficulty}</span>
                        <span>{module.lessons?.length || 0} lessons</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">{moduleProgress}%</div>
                      <div className="text-xs text-gray-500">Complete</div>
                    </div>
                    <button
                      onClick={() => toggleModuleExpansion(module.id)}
                      className="rounded-md p-1 transition-colors hover:bg-gray-100"
                    >
                      <ArrowRightIcon
                        className={`h-4 w-4 text-gray-500 transition-transform ${
                          isExpanded ? 'rotate-90' : ''
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="h-2 w-full rounded-full bg-gray-200">
                    <motion.div
                      className="h-2 rounded-full bg-blue-600 transition-all duration-500"
                      initial={{ width: 0 }}
                      animate={{ width: `${moduleProgress}%` }}
                    />
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="border-t border-gray-200"
                  >
                    <div className="bg-gray-50 p-4">
                      <h4 className="mb-3 font-medium text-gray-900">Lessons</h4>

                      {module.lessons && module.lessons.length > 0 ? (
                        <div className="space-y-2">
                          {module.lessons.map(lesson => {
                            const lessonStatus = getLessonStatus(lesson.id);

                            return (
                              <div
                                key={lesson.id}
                                className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3"
                              >
                                <div className="flex items-center gap-3">
                                  {getStatusIcon(lessonStatus)}
                                  <div>
                                    <div className="font-medium text-gray-900">{lesson.title}</div>
                                    <div className="text-sm text-gray-600">
                                      {lesson.description}
                                    </div>
                                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                                      <span>{lesson.duration}</span>
                                      <span>•</span>
                                      <span>{lesson.type}</span>
                                      {lesson.rating && (
                                        <>
                                          <span>•</span>
                                          <div className="flex items-center gap-1">
                                            <StarIcon className="h-3 w-3 text-yellow-500" />
                                            <span>{lesson.rating}</span>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2">
                                  <span
                                    className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(lessonStatus)}`}
                                  >
                                    {lessonStatus.replace('-', ' ')}
                                  </span>

                                  <div className="flex items-center gap-1">
                                    {lessonStatus !== 'completed' && (
                                      <>
                                        {lessonStatus === 'not-started' && (
                                          <button
                                            onClick={() => handleLessonAction(lesson.id, 'start')}
                                            className="rounded-md bg-blue-600 px-3 py-1 text-xs text-white transition-colors hover:bg-blue-700"
                                          >
                                            Start
                                          </button>
                                        )}
                                        {lessonStatus === 'in-progress' && (
                                          <button
                                            onClick={() =>
                                              handleLessonAction(lesson.id, 'complete')
                                            }
                                            className="rounded-md bg-green-600 px-3 py-1 text-xs text-white transition-colors hover:bg-green-700"
                                          >
                                            Complete
                                          </button>
                                        )}
                                      </>
                                    )}
                                    <button
                                      onClick={() => setSelectedLesson(lesson)}
                                      className="p-1 text-gray-400 transition-colors hover:text-blue-600"
                                      title="View lesson details"
                                    >
                                      <EyeIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="py-6 text-center">
                          <DocumentTextIcon className="mx-auto mb-2 h-12 w-12 text-gray-400" />
                          <p className="text-gray-600">No lessons available yet</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {modules.length === 0 && (
        <div className="py-12 text-center">
          <AcademicCapIcon className="mx-auto mb-4 h-16 w-16 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">No Modules Available</h3>
          <p className="text-gray-600">Check back later for training modules.</p>
        </div>
      )}
      <AnimatePresence>
        {selectedLesson && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Lesson Details</h3>
                <button
                  onClick={() => setSelectedLesson(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">{selectedLesson.title}</h4>
                  <p className="mt-1 text-gray-600">{selectedLesson.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Duration:</span>
                    <span className="ml-2 font-medium">{selectedLesson.duration}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Type:</span>
                    <span className="ml-2 font-medium">{selectedLesson.type}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Difficulty:</span>
                    <span className="ml-2 font-medium">{selectedLesson.difficulty}</span>
                  </div>
                  {selectedLesson.rating && (
                    <div>
                      <span className="text-gray-600">Rating:</span>
                      <span className="ml-2 font-medium">{selectedLesson.rating}/5</span>
                    </div>
                  )}
                </div>

                {selectedLesson.content && (
                  <div>
                    <h5 className="mb-2 font-medium text-gray-900">Content</h5>
                    <p className="text-gray-600">{selectedLesson.content}</p>
                  </div>
                )}

                {selectedLesson.resources && selectedLesson.resources.length > 0 && (
                  <div>
                    <h5 className="mb-2 font-medium text-gray-900">Resources</h5>
                    <div className="space-y-2">
                      {selectedLesson.resources.map((resource, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            {resource.title}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setSelectedLesson(null)}
                    className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Close
                  </button>
                  {getLessonStatus(selectedLesson.id) !== 'completed' && (
                    <button
                      onClick={() => {
                        const action =
                          getLessonStatus(selectedLesson.id) === 'not-started'
                            ? 'start'
                            : 'complete';
                        handleLessonAction(selectedLesson.id, action);
                        setSelectedLesson(null);
                      }}
                      className={`flex-1 rounded-md px-4 py-2 text-white transition-colors ${
                        getLessonStatus(selectedLesson.id) === 'not-started'
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {getLessonStatus(selectedLesson.id) === 'not-started'
                        ? 'Start Lesson'
                        : 'Complete Lesson'}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModuleViewer;
